import BlackButton from 'client/components/core/BlackButton'
import { MenuTargetEnum } from 'common/components/entities/Menu/enums/MenuTargetEnum'
import MenuInterface, {
  MenuItemInterface,
} from 'common/components/entities/Menu/types/MenuInterface'
import { hasSubMenu } from 'common/components/entities/Menu/utils/hasSubMenu'
import React from 'react'
import { Toggle } from 'src/editor/shared/ui-lib/toggle'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { twMerge } from 'tailwind-merge'
import { useUpdateMenuItem } from '../../hooks/use-update-menu-item'
import { BaseMenuItem } from './base-menu-item'

interface MenuItemFlatProps {
  className?: string
  index: number
  item: MenuItemInterface
  entity: MenuInterface
  isAllowedBlogLinks: boolean
  isAllowedWebsiteLinks: boolean
  itemsLength: number
  showWarningText: (menuLink: string) => string | undefined
}

export const MenuItem = ({
  className,
  index,
  item,
  entity,
  isAllowedBlogLinks,
  isAllowedWebsiteLinks,
  itemsLength,
  showWarningText,
}: MenuItemFlatProps) => {
  const {
    updateMenuItem,
    removeMenuItem,
    updateSubMenuItem,
    removeSubMenuItem,
    addSubMenuItem,
    sortDown,
    sortUp,
  } = useUpdateMenuItem({ entity })

  const { t } = useTypedTranslation()

  const updateItem = updateMenuItem(index)

  const updateText = (text: string) => updateItem({ ...item, text })

  const isSubMenuVisible = entity.menuItems[index].subMenuOpen || false

  const updateIsOpen = (isOpen: boolean) =>
    updateItem({ ...item, subMenuOpen: isOpen })

  const menuNumber = index + 1
  const isFirst = index === 0
  const isLast = index === itemsLength - 1

  return (
    <div className={twMerge('flex', className)}>
      <div className="flex w-full flex-col gap-2.5">
        <BaseMenuItem
          item={item}
          number={menuNumber}
          isMoveDownAvailable={!isLast}
          isMoveUpAvailable={!isFirst}
          updateText={updateText}
          updateLink={link => updateItem({ ...item, link })}
          remove={() => removeMenuItem(index)}
          sortUp={sortUp(index)}
          sortDown={sortDown(index)}
          isAllowedBlogLinks={isAllowedBlogLinks}
          isAllowedWebsiteLinks={isAllowedWebsiteLinks}
          warningText={showWarningText(item.link)}
          updateTarget={checked =>
            updateItem({
              ...item,
              target: checked ? MenuTargetEnum._blank : MenuTargetEnum._self,
            })
          }
        />
        <Toggle
          label={'entity_settings.menu.has_submenu'}
          onChange={() => {
            if (hasSubMenu(item)) {
              updateItem({
                ...item,
                subMenuItems: [],
              })
            } else {
              updateItem({
                ...item,
                link: '',
                target: undefined,
                subMenuItems: [
                  { text: '', link: '', target: MenuTargetEnum._self },
                ],
              })
            }
          }}
          checked={hasSubMenu(item)}
        />
        {hasSubMenu(item) && (
          <Toggle
            labelClassName="text-textPrimary"
            label={'entity_settings.menu.item.emulate_drop_down_menu_opened'}
            checked={isSubMenuVisible}
            onChange={updateIsOpen}
          />
        )}
        {hasSubMenu(item) && (
          <div className="flex flex-col gap-5 pb-5">
            {item.subMenuItems?.map((subMenuItem, subMenuItemIndex) => {
              const updateSubItem = updateSubMenuItem(index, subMenuItemIndex)

              return (
                <div className="flex w-full gap-[5px]" key={subMenuItemIndex}>
                  <i
                    style={{ lineHeight: 'inherit' }}
                    className="fas fa-arrow-turn-down-right fa-lg"
                  />
                  <BaseMenuItem
                    item={subMenuItem}
                    number={subMenuItemIndex + 1}
                    updateText={text =>
                      updateSubItem({
                        ...subMenuItem,
                        text,
                      })
                    }
                    updateLink={link => updateSubItem({ ...subMenuItem, link })}
                    updateTarget={checked =>
                      updateSubItem({
                        ...subMenuItem,
                        target: checked
                          ? MenuTargetEnum._blank
                          : MenuTargetEnum._self,
                      })
                    }
                    remove={() => removeSubMenuItem(index, subMenuItemIndex)}
                    isAllowedBlogLinks={isAllowedBlogLinks}
                    isAllowedWebsiteLinks={isAllowedWebsiteLinks}
                    warningText={showWarningText(subMenuItem.link)}
                  />
                </div>
              )
            })}
            <BlackButton onClick={() => addSubMenuItem(index)}>
              {t('entity_settings.menu.add_submenu_item')}
            </BlackButton>
          </div>
        )}
      </div>
    </div>
  )
}
